import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Popup } from '@shopline/dashboard-ui';
import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';
import ToastContext from 'contexts/Toast';
import { useQueryMerchant } from '../useMerchant';
import { useConfigurationForm, Actions } from 'contexts/ConfigurationForm';
import { useQueryRegionAndLocalesSets } from '../useRegionAndLocaleSets';
import { useMutateConfiguration } from '../useConfiguration';
import EnhancedSection from './components/EnhancedSection';
import ProductApiInputs from './ProductApiInputs';
import PreorderPeriodInputs from './PreorderPeriodInputs';
import RegionAndLocaleSetsInputs from './RegionAndLocaleSetsInputs';
import validateInputs from './validateInputs';
import isFormChanged from './isFormChanged';
import { disableUnsavedPopup, enableUnsavedPopup } from '../postMessages';
import usePermissions from 'hooks/usePermissions';
import { PopupContent } from '../components/PopupContent';
import { fieldNameToSectionId } from './constants';

const ActionBtns = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const Container = styled.div`
  ${EnhancedSection} {
    margin: 16px 0;
  }

  ${EnhancedSection}:last-child {
    margin-bottom: 24px;
  }
`;

const ConfigurationForm = () => {
  const { t } = useTranslation(['main', 'common']);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const { createUIToast } = useContext(ToastContext);
  const { refetch: refetchMerchant } = useQueryMerchant();
  const { refetch: refetchRegionAndLocaleSets } =
    useQueryRegionAndLocalesSets();
  const { state: ConfigurationFormState, dispatch } = useConfigurationForm();
  const { editingState, isSubmitting } = ConfigurationFormState;
  const { mutate: mutateConfiguration } = useMutateConfiguration({
    onSuccess: async () => {
      const [merchantResult, regionAndLocaleSetsResult] = await Promise.all([
        refetchMerchant(),
        refetchRegionAndLocaleSets(),
      ]);

      createUIToast({
        id: nanoid(),
        type: 'success',
        titleWithParams: {
          key: 'main:Saved successfully',
        },
        duration: 5000,
      });
      disableUnsavedPopup();
      dispatch(
        Actions.setCurrentState(
          merchantResult.data,
          regionAndLocaleSetsResult.data,
        ),
      );
      dispatch(Actions.setIsSubmitting(false));
    },
    onError: (error) => {
      console.error(error);
      createUIToast({
        id: nanoid(),
        type: 'alert',
        titleWithParams: {
          key: 'common:Something went wrong. Please try again.',
        },
        duration: 5000,
      });
      dispatch(Actions.setIsSubmitting(false));
    },
  });
  const memoizedIsFormChanged = useMemo(
    () => isFormChanged(ConfigurationFormState),
    [ConfigurationFormState],
  );
  const canEdit = usePermissions('merchant', 'edit');

  const handleSave = () => {
    const errors = validateInputs(editingState);
    if (errors) {
      dispatch(Actions.setFormErrors(errors));
      createUIToast({
        id: nanoid(),
        type: 'alert',
        titleWithParams: {
          key: 'main:Setup incomplete',
        },
        duration: 5000,
      });

      // scroll to the first error element
      const errorElement = document.getElementById(
        fieldNameToSectionId[Object.keys(errors)[0]],
      );
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    dispatch(Actions.setIsSubmitting(true));
    mutateConfiguration(ConfigurationFormState);
  };
  const handleCancel = () => {
    dispatch(Actions.cancelChanges());
    disableUnsavedPopup();
    setShowCancelPopup(false);
  };

  const onChangeFromForm = () => {
    enableUnsavedPopup({
      popUpTitle: t("Your edits aren't saved"),
      popUpContent: t(
        'If you leave, any unsaved changes will be lost. Please save before leaving.',
      ),
      popUpCancelText: t('Continue editing'),
      popUpConfirmText: t('Leave'),
    });
  };

  return (
    <Container>
      <ProductApiInputs onChange={onChangeFromForm} />
      {editingState.productApiEnabled && (
        <>
          <PreorderPeriodInputs onChange={onChangeFromForm} />
          <RegionAndLocaleSetsInputs onChange={onChangeFromForm} />
        </>
      )}
      {canEdit && (
        <>
          <ActionBtns>
            <Button.Default
              type="button"
              width="WIDE"
              height="LARGE"
              disabled={isSubmitting || !memoizedIsFormChanged}
              onClick={() => setShowCancelPopup(true)}
            >
              {t('Cancel', { ns: 'common' })}
            </Button.Default>
            <Button.Primary
              type="button"
              width="WIDE"
              height="LARGE"
              isLoading={isSubmitting}
              disabled={isSubmitting || !memoizedIsFormChanged}
              onClick={handleSave}
            >
              {t('Save', { ns: 'common' })}
            </Button.Primary>
          </ActionBtns>
          {showCancelPopup && (
            <Popup
              width="480px"
              title={t('Are you sure you want to cancel?')}
              showCloseBtn={true}
              onClose={() => setShowCancelPopup(false)}
              renderFooter={() => (
                <>
                  <Button.Default
                    height="LARGE"
                    onClick={() => setShowCancelPopup(false)}
                  >
                    {t('Continue editing')}
                  </Button.Default>
                  <Button.Destructive height="LARGE" onClick={handleCancel}>
                    {t('Cancel changes')}
                  </Button.Destructive>
                </>
              )}
            >
              <PopupContent>
                {t('Any unsaved changes will be lost.')}
              </PopupContent>
            </Popup>
          )}
        </>
      )}
    </Container>
  );
};

export default ConfigurationForm;
